import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, MapPin, Users, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useLang } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [project, setProject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pr, cr] = await Promise.all([
          axios.get(`${API}/social-projects/${id}`),
          axios.get(`${API}/social-categories`),
        ]);
        setProject(pr.data);
        setCategories(cr.data || []);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const catLabel = (cid) => {
    const c = categories.find(x => x.category_id === cid);
    if (!c) return cid;
    return lang === 'en' ? c.label_en : c.label_it;
  };

  if (loading) return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 pt-20 sm:pt-24 text-muted-foreground font-sans">…</div>
    </div>
  );
  if (notFound || !project) return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 pt-20 sm:pt-24">
        <Button variant="ghost" size="sm" onClick={() => navigate('/social-projects')} data-testid="sp-back"><ArrowLeft className="w-4 h-4 mr-1.5" />{t('sp_detail_back')}</Button>
        <p className="mt-6 text-muted-foreground font-sans">—</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="sp-detail-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24">
        <Button variant="ghost" size="sm" onClick={() => navigate('/social-projects')} className="mb-6" data-testid="sp-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {t('sp_detail_back')}
        </Button>
        <div className="flex items-start gap-3 mb-6">
          <Heart className="w-6 h-6 text-primary mt-1" strokeWidth={1.5} />
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-primary">{project.title}</h1>
        </div>
        <Card className="rounded-2xl border border-border">
          <CardContent className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-sans text-muted-foreground mb-1">{t('sp_card_association')}</p>
                <div className="flex items-center gap-2 text-sm font-sans font-medium"><Users className="w-4 h-4 text-primary" strokeWidth={1.5} /> {project.association_name}</div>
              </div>
              <div>
                <p className="text-xs font-sans text-muted-foreground mb-1">{t('sp_card_city')}</p>
                <div className="flex items-center gap-2 text-sm font-sans font-medium"><MapPin className="w-4 h-4 text-primary" strokeWidth={1.5} /> {project.association_city}</div>
              </div>
            </div>
            <div>
              <p className="text-xs font-sans text-muted-foreground mb-1">{t('sp_card_purpose')}</p>
              <p className="text-sm font-sans leading-relaxed whitespace-pre-wrap" data-testid="sp-detail-purpose">{project.purpose}</p>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm font-sans mb-2">
                <span className="text-muted-foreground">{t('sp_card_progress')}</span>
                <span className="font-semibold text-primary" data-testid="sp-detail-progress">{project.progress_percentage}%</span>
              </div>
              <Progress value={project.progress_percentage} className="h-2.5" />
            </div>
            {project.category_ids?.length > 0 && (
              <div>
                <p className="text-xs font-sans text-muted-foreground mb-2">{t('sp_card_categories')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {project.category_ids.map(cid => (
                    <Badge key={cid} variant="secondary" className="font-sans">{catLabel(cid)}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-sans text-muted-foreground mb-1">{t('sp_card_status')}</p>
              <Badge variant="outline" className="font-sans">{project.status === 'ACTIVE' ? t('sp_status_active') : t('sp_status_completed')}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
